import { useState } from "react";
import { useRecordLinkCopied } from "../hooks/useRecordLinkCopied";
import { useRecordNavigationClick } from "../hooks/useRecordNavigationClick";
import { useHasAccess } from "../hooks/useHasAccess";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../hooks/useSpaceDisplayName";
import { useSessionTenantOrigin } from "../hooks/useSessionTenantOrigin";
import { spaceGitPath } from "../lib/apiPaths";
import { useMakeUrl, navigate, routes } from "../lib/urls";
import { writeToClipboard } from "../lib/writeToClipboard";
import { DeleteSpaceModal } from "./DeleteSpaceModal";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { RecentlyDeletedWorkflowsModal } from "./RecentlyDeletedWorkflowsModal";
import { SubtleButton } from "./SubtleButton";

export function SpaceActionsMenu({
  space,
  onDeleted,
  showSettings = true,
}: {
  space: DisplayableSpace & { slug?: string | null };
  onDeleted?: () => void;
  showSettings?: boolean;
}) {
  const makeUrl = useMakeUrl();
  const recordClick = useRecordNavigationClick();
  const recordLinkCopied = useRecordLinkCopied();
  const canManageSpace = useHasAccess("space:manage", space.id);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [recentlyDeletedOpen, setRecentlyDeletedOpen] = useState(false);
  const displayName = useSpaceDisplayName();
  const tenantOrigin = useSessionTenantOrigin();
  const href = makeUrl(routes.space, { spaceId: space.id });
  const gitUrl = space.slug
    ? `${tenantOrigin}${spaceGitPath(space.slug)}`
    : null;

  const dropdownSections = [
    {
      items: [
        {
          type: "button" as const,
          left: <Icon name="link-2" size={16} />,
          label: "Copy link",
          onClick: () => {
            const url = tenantOrigin + href;
            void writeToClipboard(url).then(
              (ok) => ok && recordLinkCopied("space")
            );
          },
        },
        ...(gitUrl
          ? [
              {
                type: "button" as const,
                left: <Icon name="git-branch" size={16} />,
                label: "Copy git URL",
                onClick: () => {
                  void writeToClipboard(gitUrl).then(
                    (ok) => ok && recordLinkCopied("space_git")
                  );
                },
              },
            ]
          : []),
        {
          type: "button" as const,
          left: <Icon name="box" size={16} />,
          label: "Browse storage",
          onClick: () => {
            recordClick("space_menu", "browse_storage");
            navigate(routes.modal.storage, { spaceId: space.id });
          },
        },
        ...(showSettings
          ? [
              {
                type: "link" as const,
                left: <Icon name="settings-2" size={16} />,
                label: "Settings",
                to: makeUrl(routes.modal.space, { id: space.id }),
                onClick: () => recordClick("space_menu", "settings"),
              },
            ]
          : []),
        {
          type: "button" as const,
          left: <Icon name="package-2" size={16} />,
          label: "Recently deleted",
          onClick: () => {
            recordClick("space_menu", "recently_deleted");
            setRecentlyDeletedOpen(true);
          },
        },
      ],
    },
    {
      items: [
        {
          type: "button" as const,
          left: <Icon name="trash" size={16} />,
          label: "Delete",
          disabled: !canManageSpace,
          onClick: () => setConfirmDeleteOpen(true),
        },
      ],
    },
  ];

  return (
    <>
      <Dropdown
        opener={<SubtleButton icon="more" variant="secondary" />}
        align="right"
        minWidth={160}
        sections={dropdownSections}
      />
      {confirmDeleteOpen && (
        <DeleteSpaceModal
          spaceId={space.id}
          spaceName={displayName(space)}
          onClose={() => setConfirmDeleteOpen(false)}
          onDeleted={onDeleted}
        />
      )}
      <RecentlyDeletedWorkflowsModal
        spaceId={space.id}
        open={recentlyDeletedOpen}
        onClose={() => setRecentlyDeletedOpen(false)}
      />
    </>
  );
}
